/**
 * AIHelperCoachmark — a small fixed-position tooltip anchored to a DOM
 * element, used during onboarding to tell users they can tap words.
 *
 * Placed below the target by default (arrow pointing up).  Falls back to
 * above if there isn't enough space below.
 *
 * Recalculates position on scroll / resize so it stays attached.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import styles from '../../styles/AIHelper.module.css';
import FaIcon from '../FaIcon';

const MARGIN = 10; // px gap between target and coachmark
const ARROW_SIZE = 8; // must match the CSS border-width

export default function AIHelperCoachmark({ targetElement, onDismiss }) {
  const [pos, setPos] = useState(null); // { top, left, placement }
  const coachmarkRef = useRef(null);

  const recalc = useCallback(() => {
    if (!targetElement || !coachmarkRef.current) return;

    const targetRect = targetElement.getBoundingClientRect();
    const cmRect = coachmarkRef.current.getBoundingClientRect();
    const viewH = window.innerHeight;
    const viewW = window.innerWidth;

    // Horizontal: centre on the target, clamp to viewport
    let left = targetRect.left + targetRect.width / 2 - cmRect.width / 2;
    left = Math.max(8, Math.min(left, viewW - cmRect.width - 8));

    // Vertical: prefer below
    const spaceBelow = viewH - targetRect.bottom;
    const neededBelow = cmRect.height + MARGIN + ARROW_SIZE;

    let top;
    let placement;

    if (spaceBelow >= neededBelow) {
      placement = 'below';
      top = targetRect.bottom + MARGIN;
    } else {
      placement = 'above';
      top = targetRect.top - cmRect.height - MARGIN;
    }

    // Compute arrow offset relative to coachmark left edge
    const arrowLeft = targetRect.left + targetRect.width / 2 - left;

    setPos({ top, left, placement, arrowLeft });
  }, [targetElement]);

  // Recalculate on mount, scroll, resize
  useEffect(() => {
    if (!targetElement) return;

    // Initial calc after first paint (need the ref to be measured)
    const rafId = requestAnimationFrame(recalc);

    window.addEventListener('scroll', recalc, true);
    window.addEventListener('resize', recalc);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', recalc, true);
      window.removeEventListener('resize', recalc);
    };
  }, [targetElement, recalc]);

  if (!targetElement) return null;

  return (
    <div
      ref={coachmarkRef}
      className={`${styles.coachmark} ${pos ? styles.coachmarkVisible : ''} ${
        pos?.placement === 'above' ? styles.coachmarkAbove : ''
      }`}
      style={
        pos
          ? { top: `${pos.top}px`, left: `${pos.left}px` }
          : { visibility: 'hidden' }
      }
    >
      {/* Arrow */}
      <span
        className={`${styles.coachmarkArrow} ${
          pos?.placement === 'above' ? styles.coachmarkArrowDown : ''
        }`}
        style={pos?.arrowLeft != null ? { left: `${pos.arrowLeft}px` } : undefined}
      />

      {/* Content */}
      <div className={styles.coachmarkBody}>
        <img
          src="/lella.png"
          alt="Lella"
          className={styles.coachmarkAvatar}
        />
        <span className={styles.coachmarkText}>
          <FaIcon className="fa-solid fa-wand-magic-sparkles"  />
          {isMobile ? ' Tocca per scoprire di più' : ' Clicca per scoprire di più'}
        </span>
      </div>

      {/* Dismiss */}
      <button
        className={styles.coachmarkDismiss}
        onClick={onDismiss}
        aria-label="Chiudi suggerimento"
      >
        <FaIcon className="fa-solid fa-xmark"  />
      </button>
    </div>
  );
}
